class ProductMediaZoomControls extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToCarousel) {
      return;
    }

    this.carousel = document.getElementById(this.getAttribute('aria-controls'));
    if (!this.carousel) {
      return;
    }

    this.minimum = Number.parseFloat(this.dataset.min || '1');
    this.maximum = Number.parseFloat(this.dataset.max || '2');
    this.step = Number.parseFloat(this.dataset.step || '0.25');
    this.scale = this.minimum;
    this.output = this.querySelector('output');
    this.zoomInButton = this.querySelector('[data-zoom-action="in"]');
    this.zoomOutButton = this.querySelector('[data-zoom-action="out"]');
    this.handleClick = this.handleClick.bind(this);
    this.handleMediaChange = this.handleMediaChange.bind(this);

    this.addEventListener('click', this.handleClick);
    this.mediaObserver = new MutationObserver(this.handleMediaChange);
    this.mediaObserver.observe(this.carousel, {
      attributeFilter: ['media-id'],
    });

    this.isConnectedToCarousel = true;
    this.updateControls();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.mediaObserver?.disconnect();
    this.isConnectedToCarousel = false;
  }

  get activeMediaId() {
    return this.carousel?.getAttribute('media-id') || '';
  }

  get activeSlide() {
    if (!this.carousel) {
      return null;
    }

    if (this.activeMediaId) {
      return this.carousel.querySelector(
        `.media-carousel-slide[data-media-id="${CSS.escape(this.activeMediaId)}"]`,
      );
    }

    return this.carousel.querySelector('.media-carousel-slide:not([hidden])');
  }

  get activeImage() {
    return this.activeSlide?.querySelector('[data-media-type="image"]') || null;
  }

  handleClick(event) {
    const button = event.target.closest('[data-zoom-action]');
    if (!button || !this.contains(button)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const action = button.dataset.zoomAction;
    if (action === 'open') {
      if (!this.activeImage) {
        return;
      }

      this.dispatchEvent(
        new CustomEvent('lightbox:open', {
          bubbles: true,
          detail: { mediaId: this.activeMediaId },
        }),
      );
      return;
    }

    const direction = action === 'in' ? 1 : -1;
    this.scale = Math.min(
      this.maximum,
      Math.max(this.minimum, this.scale + this.step * direction),
    );
    this.applyScale();
  }

  handleMediaChange() {
    this.resetAllSlides();
    this.scale = this.minimum;
    this.updateControls();
  }

  applyScale() {
    const slide = this.activeSlide;
    if (!slide || !this.activeImage) {
      this.updateControls();
      return;
    }

    slide.style.setProperty('--product-media-zoom', String(this.scale));
    this.updateControls();
  }

  resetAllSlides() {
    this.carousel
      ?.querySelectorAll('.media-carousel-slide')
      .forEach((slide) => slide.style.removeProperty('--product-media-zoom'));
  }

  updateControls() {
    const imageAvailable = Boolean(this.activeImage);

    if (this.zoomInButton) {
      this.zoomInButton.disabled = !imageAvailable || this.scale >= this.maximum;
    }
    if (this.zoomOutButton) {
      this.zoomOutButton.disabled = !imageAvailable || this.scale <= this.minimum;
    }
    if (this.output) {
      this.output.textContent = `${Math.round(this.scale * 100)} %`;
    }
  }
}

if (!customElements.get('product-media-zoom-controls')) {
  customElements.define('product-media-zoom-controls', ProductMediaZoomControls);
}
